import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import { filterActive } from './trash';

export const PRIVATE_PRACTICE_ORG_ID = 'private-practice';
export const PRIVATE_PRACTICE_ORG_NAME = 'Private Practice';

export type OrganizationType =
  | 'government'
  | 'corporate'
  | 'bank'
  | 'ngo'
  | 'housing'
  | 'other';

export interface Organization {
  id: string;
  workspaceId: string;
  name: string;
  nameNormalized: string;
  type?: OrganizationType;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  deletedAt?: string;
}

export type OrganizationFilterId = 'all' | typeof PRIVATE_PRACTICE_ORG_ID | string;

export interface CaseOrganizationFields {
  organizationId?: string;
  organizationName?: string;
}

export function normalizeOrgName(name: string): string {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function resolveCaseOrganization(
  caseData: CaseOrganizationFields | null | undefined,
): { id: string; name: string } {
  const id = caseData?.organizationId || PRIVATE_PRACTICE_ORG_ID;
  const name =
    caseData?.organizationName ||
    (id === PRIVATE_PRACTICE_ORG_ID ? PRIVATE_PRACTICE_ORG_NAME : 'Unknown Organization');
  return { id, name };
}

export function isPrivatePracticeOrganization(
  caseData: CaseOrganizationFields | null | undefined,
): boolean {
  return resolveCaseOrganization(caseData).id === PRIVATE_PRACTICE_ORG_ID;
}

export function caseMatchesOrganizationFilter(
  caseData: CaseOrganizationFields,
  filterId: OrganizationFilterId,
): boolean {
  if (filterId === 'all') return true;
  const resolved = resolveCaseOrganization(caseData);
  return resolved.id === filterId;
}

export function organizationFieldsForCase(
  organizationId: string,
  organizationName: string,
): { organizationId: string; organizationName: string } {
  return { organizationId, organizationName };
}

export function privatePracticeCaseFields(): {
  organizationId: string;
  organizationName: string;
} {
  return organizationFieldsForCase(PRIVATE_PRACTICE_ORG_ID, PRIVATE_PRACTICE_ORG_NAME);
}

export async function fetchOrganizations(workspaceId: string): Promise<Organization[]> {
  const snap = await getDocs(
    query(collection(db, 'workspaces', workspaceId, 'organizations'), limit(200)),
  );
  const orgs: Organization[] = [];
  snap.forEach((d) => {
    orgs.push({ id: d.id, ...d.data() } as Organization);
  });
  return filterActive(orgs).sort((a, b) => a.name.localeCompare(b.name));
}

export async function findOrganizationByNormalizedName(
  workspaceId: string,
  nameNormalized: string,
  excludeId?: string,
): Promise<Organization | null> {
  const snap = await getDocs(
    query(
      collection(db, 'workspaces', workspaceId, 'organizations'),
      where('nameNormalized', '==', nameNormalized),
      limit(5),
    ),
  );
  for (const d of snap.docs) {
    if (excludeId && d.id === excludeId) continue;
    const data = d.data();
    if (data.deletedAt) continue;
    return { id: d.id, ...data } as Organization;
  }
  return null;
}

export async function countCasesForOrganization(
  workspaceId: string,
  organizationId: string,
): Promise<number> {
  const snap = await getDocs(
    query(
      collection(db, 'workspaces', workspaceId, 'cases'),
      where('organizationId', '==', organizationId),
      limit(500),
    ),
  );
  let count = 0;
  snap.forEach((d) => {
    if (!d.data().deletedAt) count += 1;
  });
  return count;
}

export async function createOrganization(
  workspaceId: string,
  userId: string,
  input: { name: string; type?: OrganizationType; notes?: string },
): Promise<Organization> {
  const trimmed = input.name.trim();
  if (!trimmed) throw new Error('Organization name is required');

  const nameNormalized = normalizeOrgName(trimmed);
  const existing = await findOrganizationByNormalizedName(workspaceId, nameNormalized);
  if (existing) throw new Error('An organization with this name already exists');

  const ref = await addDoc(collection(db, 'workspaces', workspaceId, 'organizations'), {
    workspaceId,
    name: trimmed,
    nameNormalized,
    type: input.type || 'other',
    notes: input.notes?.trim() || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId,
  });

  return {
    id: ref.id,
    workspaceId,
    name: trimmed,
    nameNormalized,
    type: input.type || 'other',
    notes: input.notes?.trim() || '',
    createdBy: userId,
  };
}

export async function updateOrganization(
  workspaceId: string,
  orgId: string,
  input: { name?: string; type?: OrganizationType; notes?: string },
): Promise<void> {
  const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };

  if (input.name !== undefined) {
    const trimmed = input.name.trim();
    if (!trimmed) throw new Error('Organization name is required');
    const nameNormalized = normalizeOrgName(trimmed);
    const existing = await findOrganizationByNormalizedName(workspaceId, nameNormalized, orgId);
    if (existing) throw new Error('An organization with this name already exists');
    patch.name = trimmed;
    patch.nameNormalized = nameNormalized;

    await syncCaseOrganizationNames(workspaceId, orgId, trimmed);
  }

  if (input.type !== undefined) patch.type = input.type;
  if (input.notes !== undefined) patch.notes = input.notes.trim();

  await updateDoc(doc(db, 'workspaces', workspaceId, 'organizations', orgId), patch);
}

async function syncCaseOrganizationNames(
  workspaceId: string,
  organizationId: string,
  organizationName: string,
): Promise<void> {
  const snap = await getDocs(
    query(
      collection(db, 'workspaces', workspaceId, 'cases'),
      where('organizationId', '==', organizationId),
      limit(500),
    ),
  );
  if (snap.empty) return;

  const batch = writeBatch(db);
  snap.forEach((d) => {
    batch.update(d.ref, { organizationName, updatedAt: serverTimestamp() });
  });
  await batch.commit();
}

export async function reassignCasesToPrivatePractice(
  workspaceId: string,
  organizationId: string,
): Promise<number> {
  const snap = await getDocs(
    query(
      collection(db, 'workspaces', workspaceId, 'cases'),
      where('organizationId', '==', organizationId),
      limit(500),
    ),
  );
  if (snap.empty) return 0;

  const batch = writeBatch(db);
  snap.forEach((d) => {
    batch.update(d.ref, {
      ...privatePracticeCaseFields(),
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
  return snap.size;
}

export async function softDeleteOrganization(
  workspaceId: string,
  orgId: string,
  userId: string,
  options?: { reassignCases?: boolean },
): Promise<void> {
  const caseCount = await countCasesForOrganization(workspaceId, orgId);
  if (caseCount > 0) {
    if (options?.reassignCases) {
      await reassignCasesToPrivatePractice(workspaceId, orgId);
    } else {
      throw new Error(
        `This organization has ${caseCount} case${caseCount === 1 ? '' : 's'}. Reassign them before deleting.`,
      );
    }
  }

  await updateDoc(doc(db, 'workspaces', workspaceId, 'organizations', orgId), {
    deletedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
    deletedBy: userId,
  });
}

export const ORGANIZATION_TYPE_LABELS: Record<OrganizationType, string> = {
  government: 'Government',
  corporate: 'Corporate',
  bank: 'Bank / Financial',
  ngo: 'NGO',
  housing: 'Housing Society',
  other: 'Other',
};

export function resolveOrganizationType(type?: string): OrganizationType {
  if (type && type in ORGANIZATION_TYPE_LABELS) {
    return type as OrganizationType;
  }
  return 'other';
}
